import Konva from 'konva'
import _ from 'lodash'

import { validateLogic } from './helpers'

/**
 * Inline merge-field token, e.g. `{{ firstName }}`.
 *
 * Deliberately the same lenient syntax the custom QR content already accepts
 * (`resolveQrContent` in BadgePreview) so authors only learn one convention.
 */
const MERGE_TOKEN = /{{\s*(.*?)\s*}}/g

/** Placeholder a repeat line's template substitutes each resolved row into. */
const REPEAT_ITEM_TOKEN = /{{\s*item\s*}}/gi

/**
 * Substitutes `value` for the `{{ item }}` placeholder in a repeat line's
 * template.
 *
 * The replacement goes through a function rather than a string on purpose.
 * `String.prototype.replace` reads `$&`, `` $` ``, `$'` and `$n` out of a
 * replacement *string* and expands them, which would mangle any attendee value
 * holding a dollar sign — a session called "Save $50" among them. A replacer
 * function is inserted literally.
 *
 * @param {string} template
 * @param {string} value
 * @returns {string} the row text
 */
const fillRepeatTemplate = (template, value) =>
  `${template}`.replace(REPEAT_ITEM_TOKEN, () => `${value}`)

export const LIST_LINE_TYPES = {
  TEXT: 'text',
  REPEAT: 'repeat',
}

/**
 * Hard cap on how many lines a single list object may be configured with.
 *
 * A guard for the editor, not the printer: nothing downstream (API, Firestore,
 * kiosk rendering) limits the line count. The line list scrolls inside the
 * settings panel, so the panel stays navigable at the cap.
 */
export const MAX_LIST_LINES = 50

/** Hard cap on the rows a single repeat line may expand to at print time. */
export const MAX_REPEAT_ROWS = 25

/** Rows a repeat line is capped at until the author changes it. */
export const DEFAULT_REPEAT_ROWS = 5

/**
 * Attrs every new list object starts with.
 *
 * `fill` is the text colour, matching Text elements; the object's own
 * background is `backgroundFill` so both are configurable.
 */
export const DEFAULT_LIST_ATTRS = {
  width: 240,
  height: 90,
  align: 'left',
  fill: '#000',
  fontSize: '16',
  fontStyle: 'normal',
  textDecoration: '',
  uppercase: false,
  lineHeight: 1.2,
  lineSpacing: 2,
  backgroundFill: 'transparent',
  stroke: '#000',
  strokeWidth: 0,
}

/**
 * Builds an empty line of the given type.
 * @param {string} type one of LIST_LINE_TYPES
 * @param {string} id unique id for the line
 * @returns {object} line configuration
 */
export const buildListLine = (type, id) => ({
  id,
  type,
  bold: false,
  // `logic` mirrors an element's `visibility`: the string 'always' or an
  // array of rules that `validateLogic` understands.
  logic: 'always',
  // Text lines
  content: '',
  // Repeat lines
  field: null,
  fieldLabel: null,
  template: '',
  maxRows: DEFAULT_REPEAT_ROWS,
})

/**
 * Replaces every `{{ accessor }}` token in `content` with the attendee's value.
 *
 * Also reports how many tokens were present and how many resolved to something
 * non-empty, which is what lets a templated line like "Pronouns: {{ pronouns }}"
 * collapse entirely rather than printing a dangling label.
 *
 * @param {string} content authored content
 * @param {func} resolveAccessor (accessor) => resolved value
 * @returns {object} { text, tokenCount, resolvedCount }
 */
const substituteTokens = (content, resolveAccessor) => {
  let tokenCount = 0
  let resolvedCount = 0

  const text = `${content ?? ''}`.replace(MERGE_TOKEN, (fullMatch, accessor) => {
    tokenCount += 1

    // Strip escaped quotes the same way the Text and QR paths do.
    const value = `${resolveAccessor(accessor.trim()) ?? ''}`.replaceAll(/[\\"]/g, '')
    if (value.trim() !== '') resolvedCount += 1

    return value
  })

  return { text, tokenCount, resolvedCount }
}

/**
 * Whether a configured line passes its own display logic.
 * @param {object} line
 * @param {object} attendee
 * @param {object} lookups option tables for validateLogic
 * @returns {boolean}
 */
const linePassesLogic = (line, attendee, lookups) => {
  if (!line.logic || line.logic === 'always') return true
  if (!_.isArray(line.logic) || line.logic.length === 0) return true

  return validateLogic(line.logic, attendee, lookups)
}

/**
 * Resolves a list object's configured lines against an attendee, producing the
 * rows that should actually print.
 *
 * Lines drop out — rather than printing blank and reserving space — when:
 * - their display logic evaluates false,
 * - every merge field on the line resolves empty, or
 * - a repeat line's source field holds nothing.
 *
 * Rows below a dropped line move up, because position is assigned during
 * layout from the surviving rows only.
 *
 * @param {object} attrs the list element's attrs (holds `lines`)
 * @param {object} attendee
 * @param {object} options { lookups, resolveAccessor, resolveListValues }
 * @returns {Array} rows of { id, text, bold }
 */
export const resolveListLines = (attrs, attendee, options) => {
  const { lookups = {}, resolveAccessor, resolveListValues } = options
  const rows = []

  _.forEach(attrs?.lines, (line) => {
    if (!linePassesLogic(line, attendee, lookups)) return

    if (line.type === LIST_LINE_TYPES.REPEAT) {
      if (!line.field) return

      const values = resolveListValues(line.field)
      if (!values || values.length === 0) return

      const cap = _.clamp(Number(line.maxRows) || DEFAULT_REPEAT_ROWS, 1, MAX_REPEAT_ROWS)

      _.forEach(_.take(values, cap), (value, i) => {
        // A template is optional — without one the row is just the value.
        const text = line.template ? fillRepeatTemplate(line.template, value) : value

        if (`${text}`.trim() === '') return

        rows.push({ id: `${line.id}:${i}`, text, bold: !!line.bold })
      })

      return
    }

    const { text, tokenCount, resolvedCount } = substituteTokens(line.content, resolveAccessor)

    // A line built entirely out of merge fields that all came back empty has
    // nothing to say — collapse it instead of printing its literal scaffolding.
    if (tokenCount > 0 && resolvedCount === 0) return
    if (text.trim() === '') return

    rows.push({ id: line.id, text, bold: !!line.bold })
  })

  return rows
}

/**
 * Builds the rows to show in the designer, where there is no attendee to
 * resolve against.
 *
 * Merge-field tokens stay visible (the same way a Text element shows
 * `{{ First Name }}`), and a repeat line renders its full `maxRows` worth of
 * placeholder rows so the author can see how much vertical space it will claim
 * at worst.
 *
 * @param {object} attrs the list element's attrs
 * @returns {Array} rows of { id, text, bold }
 */
export const previewListLines = (attrs) => {
  const rows = []

  _.forEach(attrs?.lines, (line) => {
    if (line.type === LIST_LINE_TYPES.REPEAT) {
      const token = `{{ ${line.fieldLabel || line.field || 'Select a field'} }}`
      const text = line.template ? fillRepeatTemplate(line.template, token) : token
      const cap = _.clamp(Number(line.maxRows) || DEFAULT_REPEAT_ROWS, 1, MAX_REPEAT_ROWS)

      _.forEach(_.range(cap), (i) => {
        rows.push({ id: `${line.id}:${i}`, text, bold: !!line.bold })
      })

      return
    }

    if (`${line.content ?? ''}`.trim() === '') return

    rows.push({ id: line.id, text: line.content, bold: !!line.bold })
  })

  return rows
}

/**
 * Applies a line's bold flag on top of the object's shared font style.
 * @param {string} fontStyle e.g. 'normal', 'italic', 'bold italic'
 * @param {boolean} bold
 * @returns {string} konva fontStyle
 */
const withBold = (fontStyle, bold) => {
  const style = fontStyle || 'normal'
  if (!bold || style.includes('bold')) return style
  if (style.includes('italic')) return 'bold italic'
  return 'bold'
}

/**
 * A single detached Konva.Text reused for every measurement.
 *
 * Konva registers every shape it constructs in a module-level map keyed by its
 * hit colour, so constructing one node per line per render would leak steadily.
 * One long-lived node that gets re-configured avoids that entirely, and is
 * cheaper besides.
 */
let measureNode = null

const getMeasureNode = () => {
  if (!measureNode) measureNode = new Konva.Text({ padding: 0 })
  return measureNode
}

/**
 * Positions rows within a list object, stacking each one directly beneath the
 * previous.
 *
 * Row height has to be *measured* rather than assumed: a row longer than the
 * object's width wraps onto additional visual lines, and the row beneath it has
 * to start below all of them. Konva computes the wrap in its constructor when
 * `width` is fixed and `height` is auto, so `height()` after `setAttrs` is the
 * real wrapped height.
 *
 * Nothing is truncated or shrunk to fit — content taller than `attrs.height`
 * simply extends past it, and `height` in the return value reports the space
 * actually needed so callers can flag the overflow.
 *
 * @param {object} attrs the list element's attrs
 * @param {Array} rows rows from resolveListLines / previewListLines
 * @returns {object} { rows: [{ id, text, bold, fontStyle, y, height }], height }
 */
export const layoutListLines = (attrs, rows) => {
  const fontSize = parseInt(attrs.fontSize, 10) || 16
  const lineHeight = Number(attrs.lineHeight) || 1.2
  const lineSpacing = Number(attrs.lineSpacing) || 0
  // Clearing the width input hands us NaN. Measuring against that produces
  // nonsense rather than an error, so fall back to unwrapped measurement until
  // a real width comes back.
  const width = Number(attrs.width) > 0 ? Number(attrs.width) : 'auto'
  const node = getMeasureNode()

  let offset = 0

  const laidOut = _.map(rows, (row, i) => {
    const fontStyle = withBold(attrs.fontStyle, row.bold)
    const text = attrs.uppercase ? `${row.text}`.toUpperCase() : `${row.text}`

    node.setAttrs({
      text,
      width,
      // Explicitly auto so height() reports the measured wrap, not a leftover
      // fixed height from a previous measurement.
      height: 'auto',
      wrap: 'word',
      padding: 0,
      fontSize,
      fontStyle,
      lineHeight,
      fontFamily: attrs.fontFamily || 'Arial',
      letterSpacing: Number(attrs.letterSpacing) || 0,
    })

    const height = node.height()
    const placed = { ...row, text, fontStyle, y: offset, height }

    offset += height
    if (i < rows.length - 1) offset += lineSpacing

    return placed
  })

  return { rows: laidOut, height: offset }
}
